import React, { useState, Fragment, useContext, useEffect } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  Ddql,
  DdqlFieldsFragment,
  DdqlQuery,
  PermissionsResourceType,
  useDeleteDdqlMutation,
  useUpdateDdqlMutation,
  useUpdateDdqlsMutation,
} from "../graphql/generated";
import { ProgressBar } from "./ProgressBar";
import { Avatar } from "./account/Avatar";
import clsx from "clsx";
import {
  getQuestionPriorityText,
  QuestionPriorityIcon,
} from "./QuestionPriorityIcon";
import { QuestionsFilters, QuestionStatusText } from "../routes/questions";
import { NavLink, useParams } from "react-router-dom";
import { Avatars } from "./Avatars";
import TableHeader from "./tailwind/TableHeader";
import { DdqlContext } from "../routes/questions/ddql";
import { Checkbox } from "./checkbox";
import { PermissionCheck } from "./permissions/PermissionCheck";
import { TripleDotMenu } from "./TripleDotMenu";
import { AnimatedModal } from "./AnimatedModal";
import { CloseIcon } from "./CloseIcon";
import { TextInput } from "./tailwind/TextInput";
import useGqlClient from "../hooks/useGqlClient";
import { Button } from "./tailwind/Button";
import { toasts } from "./toasts/toasts";
import { useInvalidateQueryKeys } from "../hooks/useInvalidateQueryKeys";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { reorder } from "../utils/dnd";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import {
  FileSpreadsheetIcon,
  LinkIcon,
  PencilIcon,
  TrashIcon,
} from "lucide-react";
import { PreviewDdqlModal } from "../routes/questions/ddql/PreviewDdqlImportModal";

// Extract the DDQL type from the QuestionsViewQuery
type DdqlFromQuery = DdqlQuery["ddql"];
type DdqlChildFromQuery = DdqlFromQuery["children"][0];

// Union type to handle both root DDQLs and child DDQLs
type DdqlRowData = DdqlFromQuery | DdqlChildFromQuery;

interface DdqlRowProps {
  ddql: DdqlFieldsFragment;
  rootDdql: DdqlFieldsFragment;
  depth?: number;
  expanded?: boolean;
  renderHeader?: boolean;
  navEnabled?: boolean;
  onQuestionClick: (questionId: string) => void;
  filters?: QuestionsFilters;
  skippedSheetIndexes?: number[];
  showSkip?: boolean;
  onSkipClicked?: (sheetIndex: number) => void;
}

export function DdqlRow({
  ddql,
  rootDdql,
  depth = 0,
  onQuestionClick,
  expanded,
  renderHeader,
  navEnabled,
  filters,
  showSkip,
  onSkipClicked,
  skippedSheetIndexes
}: DdqlRowProps) {
  const { selectedQuestions, setSelectedQuestions } = useContext(DdqlContext);
  const client = useGqlClient();
  const updateDdqls = useUpdateDdqlsMutation(client);
  const { invalidateAllDdqlQueries } = useInvalidateQueryKeys();

  const deleteDdql = useDeleteDdqlMutation(client);

  const [openModal, setOpenModal] = useState<
    "edit_name" | "delete" | "import" | ""
  >("");
  const [children, setChildren] = useState<DdqlFieldsFragment["children"]>(
    ddql.children
      ? [...ddql.children].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      )
      : [],
  );

  const hasEditPermission = usePermissionCheck({
    permissionKey: "questions:edit",
    resource: {
      resourceID: ddql.id,
      resourceType: PermissionsResourceType.Ddql,
    },
  });

  const [isExpanded, setIsExpanded] = useState(
    expanded !== undefined ? expanded : false,
  );

  const hasChildren = children && children.length > 0;
  const hasQuestions =
    (ddql.questions && ddql.questions.length > 0) ||
    ddql.totalChildQuestions > 0;

  const indentationStyle = {
    paddingLeft: `${depth * 24 + 10}px`, // 24px per level + base 16px
  };

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const { dealId } = useParams<{ dealId: string }>();

  if (renderHeader !== undefined && renderHeader === false) {
    depth = depth - 1;
  }

  // Sync children state when ddql.children changes
  useEffect(() => {
    if (ddql.children) {
      setChildren(
        [...ddql.children].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
        ),
      );
    }
  }, [ddql.children]);

  const filteredDdqlQuestions = ddql.questions.filter((q) => {
    if (filters && filters.createdBy) {
      return q.createdBy.id === filters.createdBy.id;
    }

    return true;
  });

  function toggleQuestion(id: string) {
    if (selectedQuestions.includes(id)) {
      setSelectedQuestions([...selectedQuestions.filter((idx) => idx !== id)]);
    } else {
      setSelectedQuestions([...selectedQuestions, id]);
    }
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const reorderedChildren = reorder(
      children,
      result.source.index,
      result.destination.index,
    );

    // Update sortOrder based on new order
    const childrenWithNewSortOrder = reorderedChildren.map((child, index) => ({
      ...child,
      sortOrder: index,
    }));

    setChildren(childrenWithNewSortOrder);

    // Update all children with new sortOrder using batch updateDdqls mutation
    updateDdqls.mutate(
      {
        input: {
          ddqlInputs: childrenWithNewSortOrder.map((child) => ({
            id: child.id,
            sortOrder: child.sortOrder,
          })),
        },
      },
      {
        onSuccess: () => {
          toasts.success("Sections reordered");
          invalidateAllDdqlQueries();
        },
        onError: (error) => {
          console.error("Error reordering sections:", error);
          toasts.error("Failed to reorder sections");
          // Revert to original order on error
          setChildren(
            ddql.children
              ? [...ddql.children].sort(
                (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
              )
              : [],
          );
        },
      },
    );
  };

  console.log(skippedSheetIndexes, ddql.sheetIndex);

  return (
    <Fragment>
      {renderHeader === undefined || renderHeader === true ? (
        <div
          className={clsx(
            "z-10 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors duration-150  border-y border-concrete-100",
            depth === 0 ? "sticky top-0" : "",
          )}
          onClick={toggleExpanded}
        >
          <div className="py-2" style={indentationStyle}>
            <div
              className={clsx(
                "flex items-center ",
                depth === 0 ? "justify-between pr-3" : "gap-x-2",
              )}
            >
              <div className="flex items-center gap-x-2">
                {(hasChildren || hasQuestions) && (
                  <button
                    onClick={toggleExpanded}
                    className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                    )}
                  </button>
                )}

                <div className="flex items-center gap-x-2">
                  {navEnabled === false ? (
                    <h3 className="text-sm font-semibold text-persian-990">
                      {ddql.name}
                    </h3>
                  ) : (
                    <NavLink to={`/deal/${dealId}/questions/lists/${ddql.id}`}>
                      <h3 className="text-sm font-semibold text-persian-990 hover:underline">
                        {ddql.name}
                      </h3>
                    </NavLink>
                  )}

                  {ddql.sheetName !== "" && showSkip && onSkipClicked && skippedSheetIndexes ? (
                    <button className="px-1.5 py-1 text-xs font-semibold text-persian-950 bg-persian-200 hover:bg-persian-300 rounded-full" onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onSkipClicked(ddql.sheetIndex);
                    }}>
                      {skippedSheetIndexes.includes(ddql.sheetIndex) ? "Include" : "Skip"}
                    </button>
                  ) : null}

                  {ddql.sheetName !== "" ? (
                    <PermissionCheck
                      input={{
                        permissionKey: "questions:edit",
                        resource: {
                          resourceID: ddql.id,
                          resourceType: PermissionsResourceType.Ddql,
                        },
                      }}
                    >
                      <button
                        className="rounded-full p-1 border border-green-200 bg-green-100 group relative"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenModal("import");
                        }}
                      >
                        <LinkIcon className="w-4 h-4 rotate-45 text-green-800" />
                        <div className="opacity-0 pointer-events-none group-hover:opacity-100 group-focus:opacity-100 transition-opacity absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap bg-black text-white text-xs rounded py-1 px-2 z-20 shadow-lg">
                          Linked to sheet in Excel file
                        </div>
                      </button>
                    </PermissionCheck>
                  ) : null}
                </div>

                <Avatars
                  accounts={
                    ddql.accessControlDealGroups
                      ? ddql.accessControlDealGroups.flatMap((acdg) =>
                        acdg.accounts.map((da) => da.dealAccount.account),
                      )
                      : []
                  }
                  max={2}
                />
              </div>
              {/* Progress or No Questions Indicator */}
              <div className="flex items-center gap-x-1.5">
                {!hasQuestions ? (
                  <p className="text-xs text-gray-500">No questions</p>
                ) : (
                  <div className="flex items-center gap-x-1.5">
                    <div className="w-32">
                      <ProgressBar
                        progress={
                          (ddql.answeredChildQuestions /
                            ddql.totalChildQuestions) *
                          100
                        }
                        height="h-1"
                        progressColor={
                          depth === 0 ? "bg-green-500" : "bg-gray-500"
                        }
                        backgroundColor={
                          depth === 0 ? "bg-green-200/80" : "bg-gray-200/80"
                        }
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      {ddql.answeredChildQuestions} / {ddql.totalChildQuestions}
                    </p>
                  </div>
                )}

                <PermissionCheck
                  input={{
                    permissionKey: "questions:edit",
                    resource: {
                      resourceID: ddql.id,
                      resourceType: PermissionsResourceType.Ddql,
                    },
                  }}
                >
                  <TripleDotMenu width="w-80">
                    {ddql.sheetName ? (
                      <TripleDotMenu.Button
                        text="Extract questions from Excel sheet"
                        variant="normal"
                        icon={
                          <FileSpreadsheetIcon className="w-4 h-4 text-concrete-600" />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenModal("import");
                        }}
                      />
                    ) : null}
                    <TripleDotMenu.Button
                      text="Edit section name"
                      icon={
                        <PencilIcon className="w-4 h-4 text-concrete-600" />
                      }
                      variant="normal"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenModal("edit_name");
                      }}
                    />
                    <TripleDotMenu.Button
                      text="Delete section"
                      icon={<TrashIcon className="w-4 h-4" />}
                      variant="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenModal("delete");
                      }}
                    />
                  </TripleDotMenu>
                </PermissionCheck>
              </div>
            </div>
          </div>
          {ddql.sourceFileName !== "" || ddql.sheetName !== "" ? (
            <PreviewDdqlModal
              open={openModal === "import"}
              ddql={ddql as Ddql}
              sourceFilePresignedUrl={rootDdql.sourceFilePresignedUrl}
              onClose={() => {
                setOpenModal("");
              }}
              key={ddql.id}
            />
          ) : null}

          <AnimatedModal
            open={openModal === "delete"}
            onClose={() => {
              setOpenModal("");
            }}
            size="lg"
            padding="0"
          >
            <div>
              <AnimatedModal.Header
                title={`Delete ${ddql.name}`}
                onClose={() => {
                  setOpenModal("");
                }}
              />
              <div className="p-6">
                <p className="text-md text-concrete-600">
                  Are you sure you want to delete this section?
                </p>

                <div className="mt-4 flex justify-end">
                  <Button
                    variant="danger"
                    type="button"
                    text="Delete section"
                    loadingText="Deleting..."
                    isLoading={deleteDdql.isPending}
                    onClick={() => {
                      if (deleteDdql.isPending) {
                        return;
                      }

                      deleteDdql.mutate(
                        {
                          id: ddql.id,
                        },
                        {
                          onSuccess: () => {
                            toasts.success(`${ddql.name} deleted`);
                            invalidateAllDdqlQueries();
                            setOpenModal("");
                          },
                          onError: () => {
                            toasts.error(`Failed to delete section`);
                            invalidateAllDdqlQueries();
                          },
                        },
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </AnimatedModal>
        </div>
      ) : null}

      {/* Expanded Content */}
      {isExpanded && (
        <div>
          {hasQuestions && filteredDdqlQuestions.length > 0 ? (
            <table className="min-w-full divide-y divide-concrete-100">
              <thead className="">
                <tr>
                  <PermissionCheck
                    input={{
                      permissionKey: "questions:edit",
                      resource: {
                        resourceID: ddql.id,
                        resourceType: PermissionsResourceType.Ddql,
                      },
                    }}
                  >
                    <TableHeader
                      padding="s xs s m"
                      className="tracking-wider whitespace-nowrap"
                      align="left"
                    >
                      <Checkbox
                        id="all-questions"
                        checked={
                          // Ensure all question ids from filteredDdqlQuestions are in selectedQuestions
                          filteredDdqlQuestions.length > 0 &&
                          filteredDdqlQuestions.every((q) =>
                            selectedQuestions.includes(q.id),
                          )
                        }
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setSelectedQuestions([
                              ...selectedQuestions,
                              ...filteredDdqlQuestions.map((q) => q.id),
                            ]);
                          } else {
                            setSelectedQuestions(
                              selectedQuestions.filter(
                                (id) =>
                                  !filteredDdqlQuestions.some(
                                    (q) => q.id === id,
                                  ),
                              ),
                            );
                          }
                        }}
                      />
                    </TableHeader>
                  </PermissionCheck>
                  <TableHeader
                    className="gap-x-2 tracking-wider flex items-center"
                    padding="m l m s"
                    style={{ width: "100%" }}
                  >
                    <p>Question</p>
                  </TableHeader>
                  <TableHeader
                    padding="m l"
                    className="tracking-wider whitespace-nowrap"
                    align="left"
                  >
                    Created by
                  </TableHeader>
                  <TableHeader
                    padding="m l"
                    className="tracking-wider whitespace-nowrap"
                    align="left"
                  >
                    Status
                  </TableHeader>
                  <TableHeader
                    padding="m l"
                    className="tracking-wider whitespace-nowrap"
                    align="left"
                  >
                    Priority
                  </TableHeader>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-concrete-100">
                {filteredDdqlQuestions.map((question) => (
                  <tr
                    key={question.id}
                    className={clsx(
                      "hover:bg-persian-50 cursor-pointer group transition-colors duration-150",
                      selectedQuestions.includes(question.id)
                        ? "bg-persian-50"
                        : "",
                    )}
                    onClick={(e) => {
                      if (selectedQuestions.length > 0) {
                        toggleQuestion(question.id);
                        return;
                      }

                      e.stopPropagation();
                      onQuestionClick(question.id);
                    }}
                  >
                    <PermissionCheck
                      input={{
                        permissionKey: "questions:edit",
                        resource: {
                          resourceID: ddql.id,
                          resourceType: PermissionsResourceType.Ddql,
                        },
                      }}
                    >
                      <td
                        className="pl-3 pr-1 py-1.5"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleQuestion(question.id);
                        }}
                      >
                        <Checkbox
                          id={question.id}
                          checked={selectedQuestions.includes(question.id)}
                          onChange={(e) => {
                            toggleQuestion(question.id);
                          }}
                        />
                      </td>
                    </PermissionCheck>
                    <td className="pl-2 py-2 text-sm max-w-0 w-full">
                      <p
                        className="truncate text-persian-990 leading-relaxed"
                        title={question.title}
                      >
                        {question.title}
                      </p>
                    </td>
                    <td className="px-4">
                      <div className="flex items-center gap-x-1.5">
                        <Avatar size="s" account={question.createdBy} />
                        <p className="truncate text-sm text-concrete-950">
                          {question.createdBy.name}
                        </p>
                      </div>
                    </td>

                    <td className="px-4">
                      <QuestionStatusText
                        status={question.status}
                        answers={question.answers as any}
                      />
                    </td>
                    <td className="px-4">
                      <div className="flex items-center gap-x-1.5">
                        <QuestionPriorityIcon priority={question.priority} />
                        <p className="truncate text-sm text-concrete-950">
                          {getQuestionPriorityText(question.priority ?? null)}
                        </p>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          {/* Recursive Children Rendering with Drag and Drop */}
          {hasChildren && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId={`ddql-children-${ddql.id}`}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {children.map((childDdql, index) => (
                      <Draggable
                        key={childDdql.id}
                        draggableId={childDdql.id}
                        index={index}
                        isDragDisabled={hasEditPermission === false}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={clsx(
                              snapshot.isDragging
                                ? "opacity-60 bg-white shadow-lg rounded-lg"
                                : "",
                            )}
                          >
                            <DdqlRow
                              ddql={childDdql as any}
                              rootDdql={rootDdql}
                              depth={depth + 1}
                              onQuestionClick={onQuestionClick}
                              expanded={expanded}
                              navEnabled={navEnabled}
                              filters={filters}
                              showSkip={showSkip}
                              onSkipClicked={onSkipClicked}
                              skippedSheetIndexes={skippedSheetIndexes}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      )}
      <UpdateDdqlModal
        ddql={ddql}
        open={openModal === "edit_name"}
        onClose={() => {
          setOpenModal("");
        }}
      />
    </Fragment>
  );
}

function UpdateDdqlModal(props: {
  ddql: DdqlFieldsFragment;
  open: boolean;
  onClose: () => void;
}) {
  const client = useGqlClient();
  const updateDdql = useUpdateDdqlMutation(client);
  const [name, setName] = useState(props.ddql.name);
  const [nameError, setNameError] = useState("");
  const { invalidateAllDdqlQueries } = useInvalidateQueryKeys();

  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="md">
      <div>
        <div className="flex items-center justify-between">
          <p className="text-persian-990 font-semibold text-md">Edit name</p>
          <CloseIcon onClose={props.onClose} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();

            if (name === "") {
              setNameError("Name cannot be empty");
            }

            updateDdql.mutate(
              {
                input: {
                  id: props.ddql.id,
                  name: name,
                },
              },
              {
                onSuccess: () => {
                  props.onClose();
                  toasts.success("Name updated");
                  invalidateAllDdqlQueries();
                },
              },
            );
          }}
        >
          <TextInput
            error={nameError}
            value={name}
            onChange={(e) => {
              setNameError("");
              setName(e.currentTarget.value);
            }}
          />

          <div className="flex mt-4 justify-end">
            <Button
              text="Update"
              variant="positive"
              type="submit"
              isLoading={updateDdql.isPending}
              isDisabled={name === "" || updateDdql.isPending}
            />
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
}
